/**
 * Backup POR CONDOMÍNIO — responsabilidade do síndico (0.1.62).
 *
 * O síndico faz backup/restauração APENAS dos condomínios sob o poder dele
 * (o Dono usa o Backup completo, do banco inteiro). Restaurar SUBSTITUI aquele(s)
 * condomínio(s) — antes, o app salva automaticamente uma cópia de segurança.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertTriangle, ArrowLeft, CheckCircle2, Download, HardDriveDownload, Loader2, RefreshCw, Upload,
} from "lucide-react";
import { useState } from "react";
import { api, ApiError, baixarArquivoPost } from "../lib/api";

interface CondItem { id: number; nome: string }

const erroDe = (e: unknown) =>
  e instanceof ApiError ? ((e.body as { detail?: string })?.detail ?? e.message) : "Não foi possível concluir.";

export function BackupCondominioPage({ onVoltar }: { onVoltar: () => void }) {
  const { data } = useQuery<{ condominios: CondItem[] }>({
    queryKey: ["lgpd", "backup-condominio", "meus"],
    queryFn: () => api.get<{ condominios: CondItem[] }>("/lgpd/backup-condominio/meus"),
  });
  const conds = data?.condominios ?? [];

  const [sel, setSel] = useState<Set<number>>(new Set());
  const [senhaBkp, setSenhaBkp] = useState("");
  const toggle = (id: number) =>
    setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const baixar = useMutation({
    mutationFn: () =>
      baixarArquivoPost(
        "/lgpd/backup-condominio/baixar",
        { senha: senhaBkp, condominio_ids: [...sel] },
        "SindicoApp-BackupCondominio.sindicobak-cond",
      ),
    onSuccess: () => setSenhaBkp(""),
  });

  // Restaurar
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [senhaRes, setSenhaRes] = useState("");
  const [modal, setModal] = useState(false);
  const [ciente, setCiente] = useState(false);
  const restaurar = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append("arquivo", arquivo as File);
      fd.append("senha", senhaRes);
      return api.postForm<{ condominios_restaurados: number[]; ignorados: { id: number; motivo: string }[] }>(
        "/lgpd/backup-condominio/restaurar-arquivo", fd,
      );
    },
    onSuccess: () => setModal(false),
  });

  const inputCls = "w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm";

  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={onVoltar} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft size={16} /> Voltar
      </button>

      <div className="flex items-center gap-2 mb-1">
        <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center"><HardDriveDownload size={20} /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Backup dos meus condomínios</h1>
          <p className="text-sm text-slate-500">Backup e restauração só dos condomínios que você gerencia.</p>
        </div>
      </div>

      <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-900 flex items-start gap-2 mt-4">
        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-600" />
        <span>
          Guarde o arquivo num <b>pendrive ou HD externo</b>. Ele é <b>cifrado com a sua senha de login</b>.
          Restaurar <b>substitui</b> aquele condomínio — mas o app salva uma cópia do estado atual antes.
        </span>
      </div>

      {/* Fazer backup */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 mt-4 space-y-3">
        <div className="flex items-center gap-2">
          <Download size={15} className="text-slate-500" />
          <span className="text-[13px] font-medium text-slate-800">Fazer backup</span>
        </div>
        {conds.length === 0 ? (
          <p className="text-sm text-slate-500">Você não gerencia nenhum condomínio.</p>
        ) : (
          <>
            <div className="space-y-1">
              {conds.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={sel.has(c.id)} onChange={() => toggle(c.id)} />
                  {c.nome}
                </label>
              ))}
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <input type="password" value={senhaBkp} onChange={(e) => setSenhaBkp(e.target.value)}
                placeholder="Sua senha (de login)" className={`${inputCls} w-56`} />
              <button type="button" onClick={() => baixar.mutate()} disabled={sel.size === 0 || !senhaBkp || baixar.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-40 text-white text-sm font-medium rounded-md">
                {baixar.isPending ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                Baixar backup
              </button>
              {baixar.isSuccess && (
                <span className="text-[12px] text-emerald-700 inline-flex items-center gap-1">
                  <CheckCircle2 size={14} /> Download iniciado — guarde num pendrive/HD.
                </span>
              )}
            </div>
            {baixar.error && <p className="text-xs text-rose-700">{erroDe(baixar.error)}</p>}
          </>
        )}
      </div>

      {/* Restaurar */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 mt-4">
        <div className="flex items-center gap-2 mb-1">
          <Upload size={15} className="text-rose-500" />
          <span className="text-[13px] font-medium text-slate-800">Restaurar backup</span>
        </div>
        <p className="text-[11px] text-slate-500 mb-2">
          Substitui o(s) condomínio(s) do arquivo (só os que você gerencia). O app salva o estado
          atual antes, para poder desfazer.
        </p>
        {restaurar.isSuccess ? (
          <div className="text-[13px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-start gap-2">
            <RefreshCw size={15} className="mt-0.5" />
            <div>
              Restaurado: {restaurar.data?.condominios_restaurados?.length ?? 0} condomínio(s).
              {(restaurar.data?.ignorados?.length ?? 0) > 0 && (
                <div className="text-[12px] text-amber-800 mt-1">
                  Ignorados: {restaurar.data!.ignorados.map((i) => `#${i.id} (${i.motivo})`).join(", ")}
                </div>
              )}
              <div className="mt-1"><b>Recarregue a página</b> para ver os dados.</div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <label className="block">
              <span className="block text-[11px] font-medium text-slate-500 mb-1">Arquivo (.sindicobak-cond)</span>
              <input type="file" accept=".sindicobak-cond" onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} className={inputCls} />
              {arquivo && <span className="block text-[11px] text-slate-500 mt-1 break-all">Selecionado: <b>{arquivo.name}</b></span>}
            </label>
            <div className="flex flex-wrap items-end gap-2">
              <input type="password" value={senhaRes} onChange={(e) => setSenhaRes(e.target.value)}
                placeholder="Senha usada no backup" className={`${inputCls} w-56`} />
              <button type="button" onClick={() => { setCiente(false); setModal(true); }} disabled={!arquivo || !senhaRes}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-40 text-sm font-medium rounded-md">
                <Upload size={14} /> Restaurar…
              </button>
            </div>
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="bg-rose-600 text-white px-5 py-4 flex items-center gap-3">
              <AlertTriangle size={22} />
              <h3 className="text-base font-semibold">Restaurar vai substituir o condomínio</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-700">
                Os dados atuais do(s) condomínio(s) do arquivo serão <b className="text-rose-700">substituídos</b>.
                Salvamos uma cópia do estado atual antes, por segurança.
              </p>
              <label className="flex items-start gap-2 text-[13px] text-slate-700">
                <input type="checkbox" checked={ciente} onChange={(e) => setCiente(e.target.checked)} className="mt-0.5" />
                Entendo que os dados atuais serão substituídos.
              </label>
              {restaurar.error && <p className="text-xs text-rose-700">{erroDe(restaurar.error)}</p>}
              <div className="flex gap-2 justify-end pt-1">
                <button type="button" onClick={() => setModal(false)} className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900">Cancelar</button>
                <button type="button" onClick={() => restaurar.mutate()} disabled={!ciente || restaurar.isPending}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white text-sm font-semibold rounded-md">
                  {restaurar.isPending ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                  Restaurar mesmo assim
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
